import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * BD-10 — what the style profile has learned from the person's own edits to
 * follow-up drafts (GET /api/bd-assistant/me/style-profile), with Reset
 * (DELETE). Shown only while the "Learn from my edits" step is on. Nothing is
 * learned invisibly: every habit the drafts follow is listed here.
 */

interface Profile {
  avoid: { text: string; meetings: number }[];
  prefer: { text: string; meetings: number }[];
  replace: { from: string; to: string; meetings: number }[];
  length: 'shorter' | 'longer' | null;
  typical_change: number | null;
}

interface StyleState {
  on: boolean;
  profile: Profile | null;
  built_at: string | null;
  edits_used: number;
  meetings_used: number;
  rules: { min_meetings: number; min_clients: number };
}

const q = (s: string) => `“${s}”`;

const BDStyleProfileCard: React.FC<{ on: boolean }> = ({ on }) => {
  const [state, setState] = useState<StyleState | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/bd-assistant/me/style-profile');
      setState(data.style);
    } catch {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (on) load(); }, [on, load]);

  const reset = async () => {
    setResetting(true);
    try {
      const { data } = await api.delete('/api/bd-assistant/me/style-profile');
      setState(data.style);
      setConfirming(false);
      toast.success('Forgotten. Only your edits from now on count.');
    } catch {
      toast.error('Could not reset — try again.');
    } finally {
      setResetting(false);
    }
  };

  if (!on) return null;
  if (loading && !state) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500" data-testid="bd-style-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading what the assistant has learned…
      </div>
    );
  }
  if (!state) return null;

  const p = state.profile;
  const rules = `A change is learned once you make it in ${state.rules.min_meetings} different meetings with at least ${state.rules.min_clients} clients.`;
  return (
    <div className="rounded-lg bg-gray-50 px-3 py-2" data-testid="bd-style-profile">
      <div className="text-sm font-medium text-gray-900">Learned from your edits</div>
      {!p ? (
        <p className="text-xs text-gray-500 mt-0.5" data-testid="bd-style-empty">Nothing yet. {rules}</p>
      ) : (
        <>
          <ul className="text-xs text-gray-700 mt-1 space-y-0.5 list-disc pl-4" data-testid="bd-style-items">
            {p.avoid.length > 0 && <li>You usually cut {p.avoid.map((a) => q(a.text)).join(', ')}</li>}
            {p.replace.map((r) => <li key={`${r.from}>${r.to}`}>You change {q(r.from)} to {q(r.to)}</li>)}
            {p.prefer.length > 0 && <li>You often add {p.prefer.map((a) => q(a.text)).join(', ')}</li>}
            {p.length === 'shorter' && <li>You keep drafts shorter (about {Math.abs(p.typical_change || 0)} words cut)</li>}
            {p.length === 'longer' && <li>You add a little more (about {p.typical_change} words)</li>}
          </ul>
          <p className="text-xs text-gray-500 mt-1">
            From {state.edits_used} edited draft{state.edits_used === 1 ? '' : 's'} across {state.meetings_used} meeting{state.meetings_used === 1 ? '' : 's'}.
            New drafts follow these. {rules}
          </p>
        </>
      )}
      <p className="text-xs text-gray-400 mt-1">Only these short phrases are kept, never your emails.</p>
      {(p || state.edits_used > 0) && (
        <div className="mt-2 text-xs">
          {!confirming ? (
            <button type="button" className="text-blue-600 underline" onClick={() => setConfirming(true)} data-testid="bd-style-reset">
              Reset
            </button>
          ) : (
            <span className="inline-flex items-center gap-2" data-testid="bd-style-confirm">
              <span className="text-gray-700">Forget what was learned? Earlier edits will not count again.</span>
              <button type="button" className="text-red-600 underline disabled:opacity-50" disabled={resetting} onClick={reset} data-testid="bd-style-reset-yes">
                Yes, reset
              </button>
              <button type="button" className="text-gray-600 underline" disabled={resetting} onClick={() => setConfirming(false)}>
                Cancel
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default BDStyleProfileCard;
